import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, ListGroup, Overlay, OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRightLeft } from "@fortawesome/free-solid-svg-icons";
import { fetchUserCasesMy } from "../../../services/httpClient";
import useApp from "../../../utils/useApp";

interface CaseOption {
  value: string;
  label: string;
}

interface CaseSwitcherProps {
  currentCaseId?: string;
  disabled?: boolean;
  onSelect: (caseId: string) => void;
}

const CaseSwitcher = ({ currentCaseId, disabled = false, onSelect }: CaseSwitcherProps) => {
  const { auth }: any = useApp();
  const [options, setOptions] = useState<CaseOption[]>([]);
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!auth?.profile?.id) return;

    fetchUserCasesMy()
      .then((res: any) => {
        const cases: any[] = res?.[0]?.data?.data ?? [];
        const hasCMInfo = cases.some((c) => c.primaryCM || c.secondaryCM);
        const mine = hasCMInfo
          ? cases.filter((c) => c.primaryCM?.user_id === auth.profile.id || c.secondaryCM?.user_id === auth.profile.id)
          : cases;

        setOptions(
          mine
            .map((c) => ({ value: String(c.id), label: c.name as string }))
            .sort((a, b) => (a.label || "").localeCompare(b.label || ""))
        );
      })
      .catch((err: any) => console.log(err));
  }, [auth?.profile?.id]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return options.filter((o) => o.value !== String(currentCaseId) && (o.label || "").toLowerCase().includes(term));
  }, [options, currentCaseId, search]);

  // keep the highlighted row visible by scrolling only the list itself; scrollIntoView would also scroll the page
  useEffect(() => {
    const list = listRef.current;
    const row = list?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    if (!list || !row) return;
    if (row.offsetTop < list.scrollTop) list.scrollTop = row.offsetTop;
    else if (row.offsetTop + row.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = row.offsetTop + row.offsetHeight - list.clientHeight;
  }, [activeIndex, filtered]);

  if (options.length < 2) return null;

  const close = () => {
    setShow(false);
    setSearch("");
    setActiveIndex(0);
  };

  const choose = (caseId: string) => {
    close();
    onSelect(caseId);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIndex]) choose(filtered[activeIndex].value);
    } else if (e.key === "Escape") {
      close();
    }
  };

  // The menu is portaled to document.body: the case sidebar is sticky with its own stacking context (z-index 0),
  // so an in-place dropdown renders underneath the page content next to it.
  return (
    <>
      <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-switch-case">Switch Case</Tooltip>}>
        <Button
          ref={buttonRef}
          className="app-primary-color button"
          disabled={disabled}
          aria-label="Switch Case"
          onClick={() => (show ? close() : setShow(true))}
        >
          <FontAwesomeIcon icon={faRightLeft} />
        </Button>
      </OverlayTrigger>
      <Overlay target={buttonRef.current} show={show} placement="bottom-start" container={document.body} rootClose onHide={close} onEntered={() => searchRef.current?.focus({ preventScroll: true })}>
        <Popover id="case-switcher-popover" style={{ minWidth: 260, maxWidth: 320, zIndex: 2000 }}>
          <Popover.Body className="p-2">
            <Form.Control ref={searchRef} size="sm" placeholder="Search My Cases" value={search} onChange={(e) => {
                setSearch(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onSearchKeyDown} className="mb-2" />
            <div ref={listRef} style={{ maxHeight: 280, overflowY: "auto", position: "relative" }}>
              {filtered.length === 0 ? (
                <div className="text-muted small px-2 py-1">No matching cases</div>
              ) : (
                <ListGroup variant="flush">
                  {filtered.map((o, i) => (
                    <ListGroup.Item
                      key={o.value}
                      action
                      active={i === activeIndex}
                      data-index={i}
                      className="text-truncate border-0 py-1"
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => choose(o.value)}
                    >
                      {o.label}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
};

export default CaseSwitcher;
